/**
 * Helpers for reading a v3 error.
 *
 * The server answers every failure with one body, `{ data: { error_code,
 * message?, validations? } }`, whichever layer raised it. Read errors through
 * these helpers rather than casting at the call site, so a future shape change
 * has one place to land.
 */

/** The error object the server nests under `data`. */
export interface ApiErrorPayload {
  error_code: string;
  message?: string;
  /** Present on `validation_error`: one entry per rejected field. */
  validations?: Record<string, { error_code: string; message: string }>;
  /** Present on `not_in_your_version`: the viewer's version's course page. */
  course_slug?: string | null;
}

interface ApiErrorEnvelope {
  data?: ApiErrorPayload;
}

function payloadOf(error: unknown): ApiErrorPayload | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const data = (error as ApiErrorEnvelope).data;
  if (typeof data !== 'object' || data === null) return undefined;
  if (typeof data.error_code !== 'string') return undefined;
  return data;
}

/**
 * The machine-readable code, or `undefined` when this isn't an API error at all
 * (a network failure, an abort, a bug in our code). Those get no fake code:
 * "the request never happened" and "the server refused it" need different
 * handling.
 */
export function errorCode(error: unknown): string | undefined {
  return payloadOf(error)?.error_code;
}

/** True when `error` is the API refusing for the given reason. */
export function isErrorCode(error: unknown, code: string): boolean {
  return errorCode(error) === code;
}

/**
 * The course page to send a viewer to when a link names a page their version of
 * the course doesn't have (`not_in_your_version`), else `undefined`. Old links
 * into a rewritten course land here instead of on a 404.
 */
export function yourVersionCourseSlug(error: unknown): string | undefined {
  const payload = payloadOf(error);
  if (payload?.error_code !== 'not_in_your_version') return undefined;
  return payload.course_slug ?? undefined;
}

/**
 * A by-slug query's `retry`: the default three tries, except for
 * `not_in_your_version`, an answer that won't change, so its redirect isn't
 * held back by the retry backoff.
 */
export function retryUnlessNotInYourVersion(
  failureCount: number,
  error: unknown,
): boolean {
  return yourVersionCourseSlug(error) === undefined && failureCount < 3;
}

/**
 * Per-field messages from a `validation_error`, ready to hang under inputs.
 * Empty for any other failure, so a form can render it unconditionally.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  const validations = payloadOf(error)?.validations;
  if (!validations) return {};
  return Object.fromEntries(
    Object.entries(validations).map(([field, detail]) => [
      field,
      detail.message,
    ]),
  );
}

/**
 * Something to show a person: the server's `message` when it sent one,
 * otherwise `fallback`. The `error_code` is never shown; it is for branching.
 */
export function errorMessage(error: unknown, fallback: string): string {
  return payloadOf(error)?.message?.trim() || fallback;
}
